/**
 * UIContext - Manages layout and interaction state
 * Based on requirements 1.3, 5.3
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import {
  UIContextState,
  UIContextAction,
  UIContextValue,
  Notification
} from '../types';

// Initial state
const initialState: UIContextState = {
  isLoading: false,
  showConfirmationDialog: false,
  showSettingsModal: false,
  sidebarCollapsed: false,
  theme: 'light',
  notifications: [],
};

// UI reducer
function uiReducer(state: UIContextState, action: UIContextAction): UIContextState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SHOW_CONFIRMATION_DIALOG':
      return {
        ...state,
        showConfirmationDialog: action.payload,
      };

    case 'SHOW_SETTINGS_MODAL':
      return {
        ...state,
        showSettingsModal: action.payload,
      };

    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        sidebarCollapsed: !state.sidebarCollapsed,
      };

    case 'SET_THEME':
      return {
        ...state,
        theme: action.payload,
      };

    case 'ADD_NOTIFICATION': {
      const notification: Notification = {
        ...action.payload,
        id: generateNotificationId(),
      };

      return {
        ...state,
        notifications: [...state.notifications, notification],
      };
    }

    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload
        ),
      };

    default:
      return state;
  }
}

// Utility function to generate unique notification IDs
function generateNotificationId(): string {
  return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Hook for responsive layout detection
function useResponsiveLayout() {
  const [screenSize, setScreenSize] = React.useState<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setScreenSize('xs');
      } else if (width < 960) {
        setScreenSize('sm');
      } else if (width < 1280) {
        setScreenSize('md');
      } else if (width < 1920) {
        setScreenSize('lg');
      } else {
        setScreenSize('xl');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);

    return () => {
      window.removeEventListener('resize', updateScreenSize);
    };
  }, []);

  return screenSize;
}

// Hook for managing notification auto-hide
function useNotificationAutoHide(
  notifications: Notification[],
  removeNotification: (id: string) => void
) {
  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [];

    notifications.forEach(notification => {
      if (notification.autoHide !== false) {
        const duration = notification.duration || 5000; // Default 5 seconds
        const timeout = setTimeout(() => {
          removeNotification(notification.id);
        }, duration);
        timeouts.push(timeout);
      }
    });

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [notifications, removeNotification]);
}

// Hook for theme persistence
function useThemePersistence(theme: 'light' | 'dark', setTheme: (theme: 'light' | 'dark') => void) {
  useEffect(() => {
    // Load theme from localStorage on mount
    const savedTheme = localStorage.getItem('hitl-ui-theme') as 'light' | 'dark' | null;
    if (savedTheme && savedTheme !== theme) {
      setTheme(savedTheme);
    }
  }, []);

  useEffect(() => {
    // Save theme to localStorage when it changes
    localStorage.setItem('hitl-ui-theme', theme);
    
    // Apply theme to document root for CSS variables
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
}

// Context creation
const UIContext = createContext<UIContextValue | undefined>(undefined);

// Provider component
interface UIProviderProps {
  children: React.ReactNode;
}

export function UIProvider({ children }: UIProviderProps) {
  const [state, dispatch] = useReducer(uiReducer, initialState);
  const screenSize = useResponsiveLayout();

  // Action creators
  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const showConfirmationDialog = useCallback((show: boolean) => {
    dispatch({ type: 'SHOW_CONFIRMATION_DIALOG', payload: show });
  }, []);

  const showSettingsModal = useCallback((show: boolean) => {
    dispatch({ type: 'SHOW_SETTINGS_MODAL', payload: show });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, []);

  const setTheme = useCallback((theme: 'light' | 'dark') => {
    dispatch({ type: 'SET_THEME', payload: theme });
  }, []);

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
  }, []);

  const removeNotification = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  }, []);

  // Enhanced context value with responsive layout information
  const contextValue: UIContextValue & { screenSize: typeof screenSize } = {
    state: {
      ...state,
      // Auto-collapse sidebar on small screens
      sidebarCollapsed: screenSize === 'xs' || screenSize === 'sm' ? true : state.sidebarCollapsed,
    },
    actions: {
      setLoading,
      showConfirmationDialog,
      showSettingsModal,
      toggleSidebar,
      setTheme,
      addNotification,
      removeNotification,
    },
    screenSize,
  };

  // Set up notification auto-hide
  useNotificationAutoHide(state.notifications, removeNotification);

  // Set up theme persistence
  useThemePersistence(state.theme, setTheme);

  return (
    <UIContext.Provider value={contextValue}>
      {children}
    </UIContext.Provider>
  );
}

// Hook for using the context
export function useUI() {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
}

// Additional utility hooks for common UI patterns

/**
 * Hook for managing loading states with automatic cleanup
 */
export function useLoadingState(initialLoading: boolean = false) {
  const { actions } = useUI();
  const [localLoading, setLocalLoading] = React.useState(initialLoading);

  const setLoading = useCallback((loading: boolean) => {
    setLocalLoading(loading);
    actions.setLoading(loading);
  }, [actions]);

  // Cleanup loading state on unmount
  useEffect(() => {
    return () => {
      if (localLoading) {
        actions.setLoading(false);
      }
    };
  }, [localLoading, actions]);

  return { loading: localLoading, setLoading };
}

/**
 * Hook for showing success/error notifications with consistent styling
 */
export function useNotifications() {
  const { actions } = useUI();

  const showSuccess = useCallback((message: string, duration?: number) => {
    actions.addNotification({
      type: 'success',
      message,
      autoHide: true,
      duration: duration || 4000,
    });
  }, [actions]);

  const showError = useCallback((message: string, duration?: number) => {
    actions.addNotification({
      type: 'error',
      message,
      autoHide: true,
      duration: duration || 6000,
    });
  }, [actions]);

  const showWarning = useCallback((message: string, duration?: number) => {
    actions.addNotification({
      type: 'warning',
      message,
      autoHide: true,
      duration: duration || 5000,
    });
  }, [actions]);

  const showInfo = useCallback((message: string, duration?: number) => {
    actions.addNotification({
      type: 'info',
      message,
      autoHide: true,
      duration: duration || 4000,
    });
  }, [actions]);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification: actions.removeNotification,
  };
}

/**
 * Hook for managing confirmation dialogs
 */
export function useConfirmationDialog() {
  const { state, actions } = useUI();
  const [confirmationConfig, setConfirmationConfig] = React.useState<{
    title?: string;
    message?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  }>({});

  const showConfirmation = useCallback((config: {
    title?: string;
    message?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  }) => {
    setConfirmationConfig(config);
    actions.showConfirmationDialog(true);
  }, [actions]);

  const hideConfirmation = useCallback(() => {
    actions.showConfirmationDialog(false);
    setConfirmationConfig({});
  }, [actions]);

  const handleConfirm = useCallback(() => {
    confirmationConfig.onConfirm?.();
    hideConfirmation();
  }, [confirmationConfig, hideConfirmation]);

  const handleCancel = useCallback(() => {
    confirmationConfig.onCancel?.();
    hideConfirmation();
  }, [confirmationConfig, hideConfirmation]);

  return {
    isOpen: state.showConfirmationDialog,
    config: confirmationConfig,
    showConfirmation,
    hideConfirmation,
    handleConfirm,
    handleCancel,
  };
}